import React, { useRef, useState } from 'react';

import { ColorPicker, ValueBox, ValueBoxGroupOptions, ValueStepper } from '@candu-ai/craft/ui';
import { getCssResetStyleMessage } from 'craft/hooks';
import { ToolboxComponentSchema } from 'craft/types';

import { ShadowProperties } from './Shadow';
import { useShadowCss } from './useShadowCss';
import { useShadowProperties } from './useShadowProperties';

interface Props {
  nodeIds: string[];
  property: ShadowProperties;
  label?: string;
  isInGroup?: ValueBoxGroupOptions;
  schema: ToolboxComponentSchema['Shadow'];
}

export const ShadowProperty = ({ nodeIds, property, label, isInGroup, schema }: Props) => {
  const { cssSelectorId, isFilter } = schema;
  const { cssKey, css = {}, setCss, styleHierarchy, hasShadowInStyleguide } = useShadowCss(
    nodeIds,
    cssSelectorId,
    isFilter,
  );
  const { values, updateValue } = useShadowProperties(css[cssKey], isFilter);
  const { values: defaultValues } = useShadowProperties(
    styleHierarchy?.fallbackValue as string,
    isFilter,
  );

  const inputRef = useRef<HTMLInputElement>();
  const [inputValue, setInputValue] = useState<number | undefined>(values[property]);

  const defaultValue = defaultValues[property];
  const canReset = defaultValue !== values[property];

  if (property === 'color') {
    return (
      <ColorPicker
        isInGroup
        value={values[property]}
        defaultValue={defaultValue}
        onResetTooltip={
          hasShadowInStyleguide && canReset
            ? getCssResetStyleMessage(styleHierarchy?.fallbackStyle)
            : undefined
        }
        onChange={(shadowColor: string | undefined) => {
          setCss({
            [cssKey]: updateValue(shadowColor || defaultValue, 'color'),
          });
        }}
      />
    );
  }

  return (
    <ValueBox label={label} isInGroup={isInGroup}>
      <ValueStepper
        ref={inputRef}
        step={1}
        name={`shadow-${property}`}
        placeholder={typeof inputValue === 'undefined' ? undefined : 'unset'}
        valueSuffix="px"
        editable
        format="inline"
        maxValue={9999}
        minValue={property === 'blur' ? 0 : -9999}
        onResetTooltip={
          hasShadowInStyleguide && canReset
            ? getCssResetStyleMessage(styleHierarchy?.fallbackStyle)
            : undefined
        }
        onReset={
          canReset
            ? () => {
                setInputValue(defaultValue);
                setCss({ [cssKey]: updateValue(defaultValue, property) });
              }
            : undefined
        }
        onChange={(newValue?: number) => {
          setInputValue(newValue);
          // only trigger change when there is no value or the value is within the accepted range
          if (
            typeof newValue === 'undefined' ||
            (((property === 'blur' && newValue >= 0) ||
              (property !== 'blur' && newValue >= -9999)) &&
              newValue <= 9999)
          ) {
            setCss({ [cssKey]: updateValue(newValue || defaultValue, property) });
          }
        }}
        onBlur={() => {
          if (typeof inputValue === 'undefined') {
            // if the value was deleted at the time of blurring, reflect actual value in the input
            setInputValue(values[property]);
          }
        }}
        value={inputValue}
      />
    </ValueBox>
  );
};
